/**
 * Built-in alias table for the closed set of US construction bid-item units.
 * Maps every normalized form (lowercase + alphanumeric only) to the canonical
 * code group. The same code group may have several canonical codes per owner
 * convention (e.g. TN vs TON) — resolution chooses whichever the owner uses.
 *
 * This is the deterministic Tier 1 backbone. Any string that lands in this
 * table never hits the AI and never asks the user — it just resolves.
 *
 * Source: 60+ years of US DOT bid sheets. Closed set. Ship once.
 */
export const CANONICAL_UNIT_ALIASES: Record<string, string[]> = {
  // Lump sum. Aliases must already be in normalized form (lowercase, no
  // punctuation) — see normalize-unit-token.ts. The lookup normalizes the
  // input before hitting the map, so anything with punctuation in the data
  // would be dead weight (e.g. "l/s" would never appear post-normalize).
  ls: ['ls', 'lumpsum', 'lump', 'lumpsm', 'lmpsum'],
  // Each
  ea: ['ea', 'each', 'ech', 'eac', 'eaches'],
  // Hour
  hr: ['hr', 'hour', 'hours', 'hrs'],
  // Day
  day: ['day', 'days', 'dy'],
  // Month
  mo: ['mo', 'month', 'months', 'mon'],

  // Length — imperial
  lf: [
    'lf',
    'linft',
    'linearft',
    'linearfoot',
    'linearfeet',
    'lnft',
    'lft',
    'linfeet',
    'feet',
    'foot',
    'ft',
  ],
  mi: ['mi', 'mile', 'miles'],
  in: ['in', 'inch', 'inches'],

  // Area — imperial
  sy: ['sy', 'sqyd', 'sqyds', 'squareyard', 'squareyards', 'sqyard', 'sqyards'],
  sf: ['sf', 'sqft', 'squarefoot', 'squarefeet', 'sqfoot'],
  ac: ['ac', 'acre', 'acres'],

  // Volume — imperial
  cy: [
    'cy',
    'cuyd',
    'cuyds',
    'cubicyard',
    'cubicyards',
    'cyd',
    'cyds',
    'cuyard',
    'cuyards',
  ],
  cf: ['cf', 'cuft', 'cubicfoot', 'cubicfeet'],
  gal: ['gal', 'gallon', 'gallons', 'gals'],

  // Weight — imperial. TON and TN are both valid canonical codes in different
  // states (TN in GA, TON elsewhere) — the resolver picks whichever the
  // owner's catalog actually carries.
  ton: [
    'ton',
    'tn',
    'tons',
    'tonne',
    'tonnes',
    'mtons',
    'mton',
    'shortton',
    'shorttons',
    'tns',
  ],
  lb: ['lb', 'lbs', 'pound', 'pounds', 'pnd', 'pnds'],
  oz: ['oz', 'ounce', 'ounces'],
};

/**
 * Reverse index built once at module load: every alias → its canonical group
 * key (e.g. "tonne" → "ton", "squareyards" → "sy"). Aliases are already in
 * normalized form (lowercase, alphanumeric only).
 */
const ALIAS_TO_GROUP: Map<string, string> = new Map();
for (const [group, aliases] of Object.entries(CANONICAL_UNIT_ALIASES)) {
  for (const alias of aliases) {
    ALIAS_TO_GROUP.set(alias, group);
  }
}

/**
 * Map a normalized token to its canonical group key, or null if not a known
 * synonym. The group key is intentionally a "concept" (`ton`, `sy`, `lf`...),
 * NOT a catalog `units.code` — owner catalogs differ on which exact code they
 * carry (TN vs TON). Callers should match the group against the owner's
 * units.code OR its variants, both normalized.
 */
export function canonicalGroupForToken(
  normalizedToken: string | null | undefined,
): string | null {
  if (!normalizedToken) {
    return null;
  }
  return ALIAS_TO_GROUP.get(normalizedToken) ?? null;
}
